// Shared change-detail popover: the floating rationale card for ONE AxisChange,
// anchored at s.tipPos (mirrors the P3 highlight tooltip — fixed card, click-away
// backdrop). Used by BOTH the P5 neutral-rewrite diff (RewriteDiff) and the C5
// reading-pane reframe trace (App), so the visually identical tracked-change spans
// share one interaction: click an edit → axis, Before/After, Why, grounded claims.

import type { CSSProperties } from "react";
import type { AxisChange, Bundle } from "../types";
import type { Telemetry } from "../telemetry";
import { INK, badgeFor, POL_LABEL, sectionLabel } from "../style";
import { SourceLink, findSource, type OpenSource } from "./SourceLink";

// A big contiguous deletion (e.g. a whole systemic-context passage removed as ONE edit) can carry a
// very long before/after; clip the popover display for readability (full text stays on hover title).
export function clip(t: string, n = 260): string {
  return t && t.length > n ? t.slice(0, n).replace(/\s+\S*$/, "") + " … [+" + (t.length - n) + " chars]" : t;
}

// compact wrapped chip for an outlet that has a readable source article
const outletChipStyle: CSSProperties = {
  fontSize: 10.5, fontWeight: 600, color: "rgba(55,53,47,.7)", background: "#fff",
  border: "1px solid rgba(55,53,47,.18)", borderRadius: 4, padding: "2px 7px", whiteSpace: "nowrap",
};

export function ChangeDetail({ ch, b, tipPos, onClose, groundedClaim, setGroundedClaim, tel, onOpenSource, fromChange }: {
  ch: AxisChange;
  b: Bundle;
  tipPos: { top: number; left: number };
  onClose: () => void;
  groundedClaim: string | null;
  setGroundedClaim: (id: string | null) => void;
  tel: Telemetry;
  onOpenSource?: OpenSource;
  fromChange: number | null; // change index, for grounded_claim_open telemetry
}) {
  const gC = (groundedClaim != null && ch.fact_claim_ids.includes(groundedClaim))
    ? b.fact_table.claims.find((x) => x.claim_id === groundedClaim) : null;

  return (
    <>
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 50 }} />
    <div className="lenspop" style={{ position: "fixed", top: tipPos.top, left: tipPos.left, zIndex: 51, width: 380, maxWidth: "calc(100vw - 24px)", maxHeight: "calc(100vh - 24px)", overflowY: "auto", background: "#fff", border: "1px solid rgba(55,53,47,.14)", borderTop: "3px solid " + INK, borderRadius: 7, boxShadow: "0 14px 40px rgba(15,15,15,.16)", padding: "15px 17px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 9 }}>
        <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: ".05em", textTransform: "uppercase", color: INK, background: "#e7e6e1", padding: "3px 8px", borderRadius: 4 }}>{ch.axis}</span>
        <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(55,53,47,.4)", fontSize: 16, cursor: "pointer", lineHeight: 1, padding: 0 }}>×</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 10 }}>
        <div><div style={{ ...sectionLabel, color: "rgba(55,53,47,.45)", marginBottom: 3 }}>Before</div><div title={ch.before} style={{ fontSize: 13, lineHeight: 1.5, color: "rgba(55,53,47,.6)" }}>{clip(ch.before)}</div></div>
        <div><div style={{ ...sectionLabel, color: INK, fontWeight: 700, marginBottom: 3 }}>After</div><div title={ch.after} style={{ fontSize: 13, lineHeight: 1.5, color: INK }}>{clip(ch.after)}</div></div>
      </div>
      <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: "rgba(55,53,47,.72)" }}><span style={{ fontWeight: 700, color: INK }}>Why:</span> {ch.why}</p>

      {ch.fact_claim_ids.length > 0 && (
        <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11.5, color: "rgba(55,53,47,.55)" }}>Grounded in</span>
          {ch.fact_claim_ids.map((id) => {
            const on = groundedClaim === id;
            const claim = b.fact_table.claims.find((x) => x.claim_id === id);
            return (
              <button key={id} title={claim?.text || id}
                onClick={() => { const was = groundedClaim === id; if (!was) tel.log("grounded_claim_open", { claim_id: id, from_change: fromChange }); setGroundedClaim(was ? null : id); }}
                style={{ font: "inherit", fontFamily: "'IBM Plex Mono',ui-monospace,monospace", fontSize: 11, fontWeight: 600, borderRadius: 4, padding: "2px 7px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4, background: on ? INK : "#fff", color: on ? "#fff" : INK, border: "1px solid " + (on ? INK : "rgba(55,53,47,.22)") }}>
                {id} ⓘ
              </button>
            );
          })}
        </div>
      )}

      {gC && (() => {
        const gBadge = badgeFor(gC.attribution_status);
        return (
          <div className="lenspop" style={{ marginTop: 10, background: "#fff", border: "1px solid rgba(55,53,47,.16)", borderRadius: 5, padding: "12px 13px" }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: INK, lineHeight: 1.4 }}>{gC.text}</span>
              <button onClick={() => setGroundedClaim(null)} style={{ background: "none", border: "none", color: "rgba(55,53,47,.4)", fontSize: 15, cursor: "pointer", lineHeight: 1, padding: 0, flex: "none" }}>×</button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", marginBottom: 9 }}>
              <span style={{ fontFamily: "'IBM Plex Mono',ui-monospace,monospace", fontSize: 10, color: "rgba(55,53,47,.5)" }}>{gC.claim_id}</span>
              <span style={gBadge.style}>{gBadge.label}</span>
              <span style={{ fontSize: 10.5, color: "rgba(55,53,47,.45)", fontWeight: 500 }}>in {gC.corroborating_outlets.length}/{b.incident.outlets.length} outlets</span>
              {gC.corroborating_outlets.filter((o) => findSource(b.sources, o)).map((o) => (
                <span key={o} style={outletChipStyle}>
                  <SourceLink outlet={o} sources={b.sources} meta={{ from: "grounded_claim", claim_id: gC.claim_id }} onOpen={onOpenSource} />
                </span>
              ))}
            </div>
            <div style={{ fontSize: 12, lineHeight: 1.55, color: "rgba(55,53,47,.65)" }}>
              <div style={{ marginBottom: 5 }}><span style={{ color: "rgba(55,53,47,.42)" }}>Asserted by</span> {gC.asserted_by.join(", ")}</div>
              {gC.contested_by.length > 0 && <div style={{ marginBottom: 5 }}><span style={{ color: "rgba(55,53,47,.42)" }}>Contested by</span>{" "}
                {gC.contested_by.map((o, i) => (
                  <span key={o + i}>{i > 0 ? ", " : ""}<SourceLink outlet={o} sources={b.sources} meta={{ from: "grounded_claim", claim_id: gC.claim_id }} onOpen={onOpenSource} /></span>
                ))}</div>}
              <div style={{ marginBottom: 7 }}><span style={{ color: "rgba(55,53,47,.42)" }}>Frame</span> {POL_LABEL[gC.frame_polarity]}</div>
              <div style={{ background: "#f7f6f3", borderLeft: "2px solid rgba(55,53,47,.2)", padding: "7px 10px", borderRadius: "0 4px 4px 0", fontFamily: "'Source Sans 3',serif", fontStyle: "italic", color: "rgba(55,53,47,.6)" }}>"{gC.verbatim_support}"</div>
            </div>
          </div>
        );
      })()}
    </div>
    </>
  );
}
